/**
 * Detection Rules v2 — YARA per-type hooks + types.
 *
 * Backing routes: cmd/admin-bff/rules_yara.go. All paths prefixed at /api/v2.
 *
 * Query key convention:
 *   ["rules-yara", "list", filters]
 *   ["rules-yara", "detail", id]
 *   ["rules-yara", "dry-runs", id]
 *   ["rules-yara", "families"]
 *   ["rules-yara", "corpora"]
 *   ["rules-yara", "imports"]
 *   ["rules-yara", "imports", id]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { LintMessage, Rule, RuleSeverity, RuleTLP, RuleVersion } from "./rules";

// ---------- Types ----------

export type ScanContext =
    | "file"
    | "memory"
    | "process"
    | "network"
    | "phishing_kit"
    | "package";

export type Confidence = "low" | "medium" | "high";

export const SCAN_CONTEXTS: ReadonlyArray<{ value: ScanContext; label: string }> = [
    { value: "file", label: "File" },
    { value: "memory", label: "Memory" },
    { value: "process", label: "Process" },
    { value: "network", label: "Network" },
    { value: "phishing_kit", label: "Phishing kit" },
    { value: "package", label: "Package" },
];

/**
 * Phase 2.O REDUX — product target taxonomy.
 *
 * `dmx` = Email security gateway (DataFlowX Mail)
 * `dsx` = Sandbox (DataFlowX Sandbox)
 * `dfx` = Endpoint agent (DataFlowX Client)
 * `any` = rule-level fan-out tag; included in every product bundle.
 *
 * `any` is never a bundle slot on its own — it piggy-backs onto whichever
 * product bundle is being composed via the `'any' = ANY(product_applicability)`
 * OR leg on the pick SQL.
 */
export type ProductTarget = "dmx" | "dsx" | "dfx" | "any";

/** Products that participate in the compile fan-out matrix (dmx/dsx/dfx). */
export type CompilableProductTarget = "dmx" | "dsx" | "dfx";

export const PRODUCT_TARGETS: ReadonlyArray<{
    value: ProductTarget;
    label: string;
    hint: string;
    /** token-based chip class; `any` renders as a muted "ALL" pill. */
    chipClass: string;
}> = [
    { value: "dmx", label: "DMX", hint: "Email security gateway", chipClass: "chip--accent" },
    { value: "dsx", label: "DSX", hint: "Sandbox (superset)", chipClass: "chip--primary" },
    { value: "dfx", label: "DFX", hint: "Endpoint agent", chipClass: "chip--info" },
    { value: "any", label: "ALL", hint: "Cross-product fan-out tag", chipClass: "chip--subtle" },
];

/** 3-item list for the compile grid (dmx/dsx/dfx); excludes `any`. */
export const COMPILABLE_PRODUCT_TARGETS: ReadonlyArray<{
    value: CompilableProductTarget;
    label: string;
    hint: string;
}> = [
    { value: "dmx", label: "DMX", hint: "Email security gateway" },
    { value: "dsx", label: "DSX", hint: "Sandbox (superset)" },
    { value: "dfx", label: "DFX", hint: "Endpoint agent" },
];

export interface YaraMeta {
    scanContext: ScanContext;
    malwareFamily?: string;
    campaign?: string;
    aptGroup?: string;
    confidence: Confidence;
    references: string[];
    sampleHashes: string[];
    imports: string[];
    externalVariables: string[];
    privateRule: boolean;
    globalRule: boolean;
    yaraVersionMin?: string;
    ruleDate?: string;
    parsedCondition?: string;
    productApplicability: string[];
}

export interface YaraListRow {
    id: string;
    slug: string;
    name: string;
    severity: string;
    status: string;
    currentVersion: number;
    publishedVersion?: number;
    tags: string[];
    imported: boolean;
    license?: string;
    updatedAt: string;
    scanContext: ScanContext;
    malwareFamily?: string;
    productApplicability: string[];
    privateRule: boolean;
    globalRule: boolean;
    // Phase 2.L.2 — IR-ID-led list + synthesized title + source-registry chip.
    intelRoomId: string;
    displayName: string;
    displayNameLocked: boolean;
    sourceRegistry?: string;
    // Phase 2.M — per-rule quality signals. `null` means "no signal yet"
    // (pre-migration or unscored); rendered as a muted dash in the UI.
    // `undefined` only shows up on clients talking to a pre-2.M backend.
    quality?: number | null;
    score?: number | null;
    importance?: number | null;
}

/**
 * Phase 2.M — one row of detection_rule_quality_overrides. Drives the
 * per-rule "Manage overrides" modal on the detail page and the global
 * catalog view at `/rules/quality-overrides`.
 *
 * kind is one of `quality_delta | score_override | importance | quality_override`
 * (see ValidOverrideKinds backend-side); the UI surfaces the full set.
 * source is free-form text — operator writes go through as `"operator"`;
 * seed rows keep `"yara-forge-noisy"` / `"yara-forge-importance"`.
 */
export type YaraOverrideKind =
    | "quality_delta"
    | "score_override"
    | "importance"
    | "quality_override";

export const OVERRIDE_KINDS: ReadonlyArray<{ value: YaraOverrideKind; label: string; hint: string }> = [
    { value: "quality_delta", label: "Quality delta", hint: "Signed offset added to computed quality (e.g. -90 to suppress)." },
    { value: "quality_override", label: "Quality override", hint: "Absolute quality value (0..100); trumps registry + delta." },
    { value: "score_override", label: "Score override", hint: "Absolute score value (0..100); bypasses the quality/importance blend." },
    { value: "importance", label: "Importance", hint: "Absolute importance value (0..100); raises the bundle tier floor." },
];

export interface YaraQualityOverride {
    ruleId: string;
    kind: YaraOverrideKind | string;
    value: number;
    reason: string;
    source: string;
    createdBy?: string;
    createdAt: string;
    updatedAt: string;
}

export interface YaraListFilters {
    q?: string;
    scanContext?: ScanContext | "";
    /** Phase 2.L.2 — multi-select pills (OR over set). */
    scanContexts?: ScanContext[];
    /** Phase 2.L.2 — multi-select source-registry filter. */
    sourceRegistries?: string[];
    malwareFamily?: string;
    productApplicability?: string;
    tag?: string;
    status?: string;
    imported?: boolean;
    limit?: number;
    offset?: number;
}

/**
 * Phase 2.L — one entry of the skip-and-continue diagnostic array backed by
 * detection_rules.quality_issues (jsonb). Producers coin their own `kind`
 * codes; the UI specialises on `"compile.error"` to drive the detail page's
 * danger banner. Other kinds fall through to the muted issues list.
 */
export interface YaraQualityIssue {
    kind: string;
    severity?: number;
    message: string;
    compiledAt?: string;
}

export interface YaraDetail {
    rule: Rule;
    yaraMeta: YaraMeta;
    currentVersion: RuleVersion;
    recentDryRuns: Array<{
        id: string;
        version: number;
        corpusSlug?: string;
        status: string;
        matches: number;
        elapsedMs: number;
        startedAt: string;
        engine: string;
    }>;
    // Phase 2.L.2 — operator-curated title + lock state.
    displayName: string;
    displayNameLocked: boolean;
    // Phase 2.L — skip-and-continue diagnostics surfaced by the importer /
    // bundle compiler. Empty array when the rule has no open issues. Marked
    // optional so older clients talking to a freshly-deployed backend (or a
    // backend predating the migration) don't runtime-error on the missing
    // field — callers default to `[]` at render time.
    qualityIssues?: YaraQualityIssue[];
    // Phase 2.M — quality signals + overrides catalog for this rule. `quality`,
    // `score`, `importance` may be NULL when no signal is computed yet.
    overrides?: YaraQualityOverride[];
    quality?: number | null;
    score?: number | null;
    importance?: number | null;
}

export interface YaraCorpus {
    slug: string;
    name: string;
    kind: string;
    description: string;
    itemCount: number;
    objectUrl: string;
}

export interface YaraImportRow {
    id: string;
    registrySlug: string;
    temporalWorkflowId?: string;
    temporalRunId?: string;
    status: "running" | "ok" | "error" | "cancelled";
    counters: Record<string, unknown>;
    startedAt: string;
    finishedAt?: string;
    elapsedMs: number;
}

export interface YaraDryRunMatch {
    sample: string;
    rule: string;
    tags: string[];
    strings: string[];
}
export interface YaraDryRunResult {
    engine: string;
    matches: YaraDryRunMatch[];
    total: number;
    elapsedMs: number;
    warnings?: string[];
}

// ---------- Queries ----------

export function useYaraList(filters: YaraListFilters = {}) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        if (Array.isArray(v)) {
            if (v.length === 0) continue;
            p.set(k, v.join(","));
            continue;
        }
        p.set(k, String(v));
    }
    return useQuery({
        queryKey: ["rules-yara", "list", filters] as const,
        queryFn: () =>
            api.get<{ rules: YaraListRow[]; total: number }>(`/api/v2/rules/yara?${p.toString()}`),
    });
}

export function useYaraDetail(id: string | undefined) {
    return useQuery({
        queryKey: ["rules-yara", "detail", id] as const,
        queryFn: () => api.get<YaraDetail>(`/api/v2/rules/yara/r/${id}`),
        enabled: !!id,
    });
}

export function useYaraDryRuns(id: string | undefined) {
    return useQuery({
        queryKey: ["rules-yara", "dry-runs", id] as const,
        queryFn: () =>
            api.get<{ dryRuns: YaraDetail["recentDryRuns"] }>(`/api/v2/rules/yara/r/${id}/dry-runs`),
        enabled: !!id,
    });
}

/**
 * Phase 2.L.5 — corpus-wide YARA summary. Always unfiltered; powers the
 * hero cards on `/rules/yara`. Refetches on window focus so "Re-classify
 * all" / import workflows update the cards without a manual reload.
 */
export interface YaraStats {
    total: number;
    imported: number;
    authored: number;
    published: number;
    draft: number;
    byScanContext: Record<string, number>;
    byRegistry: Record<string, number>;
    bySeverity: Record<string, number>;
}

export function useYaraStats() {
    return useQuery({
        queryKey: ["rules-yara", "stats"] as const,
        queryFn: () => api.get<YaraStats>(`/api/v2/rules/yara/stats`),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });
}

export function useYaraFamilies() {
    return useQuery({
        queryKey: ["rules-yara", "families"] as const,
        queryFn: () => api.get<{ families: string[] }>(`/api/v2/rules/yara/families`).then((d) => d.families ?? []),
        staleTime: 30_000,
    });
}

export function useYaraCorpora() {
    return useQuery({
        queryKey: ["rules-yara", "corpora"] as const,
        queryFn: () => api.get<{ corpora: YaraCorpus[] }>(`/api/v2/rules/yara/corpora`).then((d) => d.corpora ?? []),
        staleTime: 60_000,
    });
}

export function useYaraImports() {
    return useQuery({
        queryKey: ["rules-yara", "imports"] as const,
        queryFn: () => api.get<{ imports: YaraImportRow[] }>(`/api/v2/rules/yara/imports`).then((d) => d.imports ?? []),
        refetchInterval: 5_000,
    });
}

/**
 * Phase 2.L.1 — poll the imports list filtered by bulk_id. The progress view
 * inside the bulk-import dialog uses this to render a per-registry status
 * table; polling stops once every row is terminal (handled by the caller).
 */
export function useYaraBulkImports(bulkId: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: ["rules-yara", "imports", "bulk", bulkId] as const,
        enabled: !!bulkId && enabled,
        queryFn: () =>
            api.get<{ imports: YaraImportRow[] }>(`/api/v2/rules/yara/imports?bulkId=${bulkId}`).then(
                (d) => d.imports ?? [],
            ),
        refetchInterval: enabled ? 2_000 : false,
    });
}

export function useYaraImport(id: string | undefined) {
    return useQuery({
        queryKey: ["rules-yara", "imports", id] as const,
        queryFn: () => api.get<{ import: YaraImportRow }>(`/api/v2/rules/yara/imports/${id}`).then((d) => d.import),
        enabled: !!id,
        refetchInterval: 3_000,
    });
}

// ---------- Mutations ----------

export interface CreateYaraInput {
    slug?: string;
    name: string;
    description: string;
    severity: RuleSeverity;
    tlp: RuleTLP;
    tags: string[];
    content: string;
    meta: {
        scanContext: ScanContext;
        malwareFamily?: string;
        campaign?: string;
        aptGroup?: string;
        confidence?: Confidence;
        yaraVersionMin?: string;
        productApplicability: string[];
    };
}

export function useCreateYara() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateYaraInput) =>
            api.post<{
                rule: Rule;
                yaraMeta: YaraMeta;
                lintMessages: LintMessage[];
            }>(`/api/v2/rules/yara`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "list"] });
        },
    });
}

export interface PatchYaraMetaInput {
    scanContext?: ScanContext;
    malwareFamily?: string;
    campaign?: string;
    aptGroup?: string;
    confidence?: Confidence;
    yaraVersionMin?: string;
    productApplicability?: string[];
    privateRule?: boolean;
    globalRule?: boolean;
    /** Phase 2.L.2 — sets display_name; auto-locks unless displayNameLocked=false sent. */
    displayName?: string;
    displayNameLocked?: boolean;
}

export function usePatchYaraMeta(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchYaraMetaInput) =>
            api.patch<{ yaraMeta: YaraMeta }>(`/api/v2/rules/yara/r/${id}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "detail", id] });
        },
    });
}

export function useSaveYaraContent(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { content: string; note?: string }) =>
            api.put<{ version: RuleVersion; lintMessages: LintMessage[] }>(
                `/api/v2/rules/yara/r/${id}/content`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "list"] });
        },
    });
}

export function useLintYara(id: string | undefined) {
    return useMutation({
        mutationFn: (input: { content: string }) =>
            api.post<{ lintMessages: LintMessage[] }>(`/api/v2/rules/yara/r/${id}/lint`, input),
    });
}

export function useDryRunYara(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { corpusSlug: string; maxSamples?: number; timeoutSec?: number; version?: number }) =>
            api.post<{ result: YaraDryRunResult }>(`/api/v2/rules/yara/r/${id}/dry-run`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "dry-runs", id] });
        },
    });
}

export function useStartYaraImport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { registrySlug: string }) =>
            api.post<{ importId: string; registrySlug: string; status: string; startedAt: string }>(
                `/api/v2/rules/yara/imports`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "imports"] });
        },
    });
}

export interface BulkYaraImportInput {
    registrySlugs: string[];
    concurrency?: number;
}

export interface BulkYaraImportResponse {
    bulkId: string;
    concurrency: number;
    imports: Array<{ registrySlug: string; importId: string }>;
}

/**
 * Phase 2.L.1 — fan-out import across multiple registries. Returns a bulkId
 * the caller polls via useYaraBulkImports to render progress.
 */
export function useStartBulkYaraImport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: BulkYaraImportInput) =>
            api.post<BulkYaraImportResponse>(`/api/v2/rules/yara/imports/bulk`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "imports"] });
        },
    });
}

/**
 * Phase 2.L.2 — bulk re-classify scan_context for one or more YARA rules.
 * The IR ID stays put (immutable identity); only the bucket changes, plus
 * unlocked display_name suffixes get re-synthesized server-side.
 */
export interface BulkSetScanContextInput {
    ruleIds: string[];
    scanContext: ScanContext;
}

export function useBulkSetYaraScanContext() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: BulkSetScanContextInput) =>
            api.patch<{ updated: number }>(`/api/v2/rules/yara/bulk/scan-context`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "list"] });
        },
    });
}

// ---------- Quality overrides (Phase 2.M) ----------

/**
 * Fetch every override attached to one rule. Drives the "Manage overrides"
 * modal on the detail page. Cached under the rules-yara detail key
 * so an invalidation of the detail also refreshes this list.
 */
export function useYaraOverrides(id: string | undefined) {
    return useQuery({
        queryKey: ["rules-yara", "overrides", id] as const,
        enabled: !!id,
        queryFn: () =>
            api
                .get<{ overrides: YaraQualityOverride[] }>(
                    `/api/v2/rules/yara/r/${id}/overrides`,
                )
                .then((d) => d.overrides ?? []),
    });
}

export interface UpsertYaraOverrideInput {
    kind: YaraOverrideKind | string;
    value: number;
    reason?: string;
}

/**
 * Upsert one override for a rule. Source is hard-coded to "operator"
 * server-side; createdBy comes from auth. Invalidates both the detail
 * cache (so signal chips refresh) and the list cache (so the row's
 * quality/score/importance columns refresh).
 */
export function useUpsertYaraOverride(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: UpsertYaraOverrideInput) =>
            api.post<{ override: YaraQualityOverride }>(
                `/api/v2/rules/yara/r/${id}/overrides`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "overrides", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "list"] });
        },
    });
}

/**
 * Remove one override by kind. Idempotent — deleting a missing kind
 * returns 200. The kind is URL-escaped so future kinds with special
 * characters survive the trip; today's kinds are all safe ASCII.
 */
export function useDeleteYaraOverride(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (kind: string) =>
            api.delete<{ ok: boolean }>(
                `/api/v2/rules/yara/r/${id}/overrides/${encodeURIComponent(kind)}`,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules-yara", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "overrides", id] });
            void qc.invalidateQueries({ queryKey: ["rules-yara", "list"] });
        },
    });
}

export interface QualityOverridesCatalogFilters {
    source?: string;
    kind?: string;
    ruleId?: string;
    limit?: number;
    offset?: number;
}

/**
 * Global override catalog. Powers the optional /rules/quality-overrides
 * admin page. Filters compose AND-wise server-side; empty filter values
 * are no-ops. `total` is the unpaginated count.
 */
export function useQualityOverridesCatalog(filters: QualityOverridesCatalogFilters = {}) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        p.set(k, String(v));
    }
    return useQuery({
        queryKey: ["rules-yara", "quality-overrides", filters] as const,
        queryFn: () =>
            api.get<{ overrides: YaraQualityOverride[]; total: number }>(
                `/api/v2/rules/quality-overrides?${p.toString()}`,
            ),
    });
}

// ---------- Constants ----------
//
// Phase 2.L removed the hardcoded YARA_REGISTRIES list. The dialog now reads
// the catalog from /api/v2/registries via useRegistries({type:"yara"}).
